import { db } from './firebase'
import { collection, doc, getDoc, query, where, getDocs, orderBy, limit } from 'firebase/firestore'

class EmailStatsService {
  constructor() {
    this.emailStatsCollection = 'emailStats'
  }

  // Get email statistics for a specific month
  async getEmailStats(month = null) {
    try {
      const targetMonth = month || new Date().toISOString().slice(0, 7) // '2025-01'
      const emailStatsRef = doc(db, this.emailStatsCollection, targetMonth)
      const emailStatsDoc = await getDoc(emailStatsRef)
      
      if (emailStatsDoc.exists()) {
        const data = emailStatsDoc.data()
        return {
          month: targetMonth,
          totalEmails: data.totalEmails || 0,
          alertEmails: data.alertEmails || 0,
          organizationRequestEmails: data.organizationRequestEmails || 0,
          testEmails: data.testEmails || 0,
          otherEmails: data.otherEmails || 0,
          byOrganization: data.byOrganization || {},
          byRecipient: data.byRecipient || {},
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        }
      } else {
        return {
          month: targetMonth,
          totalEmails: 0,
          alertEmails: 0,
          organizationRequestEmails: 0,
          testEmails: 0,
          otherEmails: 0,
          byOrganization: {},
          byRecipient: {},
          createdAt: null,
          updatedAt: null
        }
      }
    } catch (error) {
      console.error('❌ Error getting email stats:', error)
      throw error
    }
  }

  // Get email statistics for multiple months
  async getEmailStatsRange(startMonth, endMonth) {
    try {
      const stats = []
      const currentDate = new Date()
      const startDate = new Date(startMonth + '-01')
      const endDate = new Date(endMonth + '-01')
      
      // Generate month range
      const months = []
      let current = new Date(startDate)
      while (current <= endDate) {
        months.push(current.toISOString().slice(0, 7))
        current.setMonth(current.getMonth() + 1)
      }
      
      // Get stats for each month
      for (const month of months) {
        const monthStats = await this.getEmailStats(month)
        stats.push(monthStats)
      }
      
      return stats
    } catch (error) {
      console.error('❌ Error getting email stats range:', error)
      throw error
    }
  }

  // Get total email statistics across all time
  async getTotalEmailStats() {
    try {
      const emailStatsQuery = query(
        collection(db, this.emailStatsCollection),
        orderBy('createdAt', 'desc')
      )
      
      const emailStatsSnapshot = await getDocs(emailStatsQuery)
      
      let totalEmails = 0
      let totalAlertEmails = 0
      let totalOrganizationRequestEmails = 0
      let totalTestEmails = 0
      let totalOtherEmails = 0
      const allByOrganization = {}
      const allByRecipient = {}
      
      emailStatsSnapshot.forEach(doc => {
        const data = doc.data()
        totalEmails += data.totalEmails || 0
        totalAlertEmails += data.alertEmails || 0
        totalOrganizationRequestEmails += data.organizationRequestEmails || 0
        totalTestEmails += data.testEmails || 0
        totalOtherEmails += data.otherEmails || 0
        
        // Aggregate by organization
        if (data.byOrganization) {
          Object.entries(data.byOrganization).forEach(([orgId, count]) => {
            allByOrganization[orgId] = (allByOrganization[orgId] || 0) + count
          })
        }
        
        // Aggregate by recipient
        if (data.byRecipient) {
          Object.entries(data.byRecipient).forEach(([email, count]) => {
            allByRecipient[email] = (allByRecipient[email] || 0) + count
          })
        }
      })
      
      return {
        totalEmails,
        alertEmails: totalAlertEmails,
        organizationRequestEmails: totalOrganizationRequestEmails,
        testEmails: totalTestEmails,
        otherEmails: totalOtherEmails,
        byOrganization: allByOrganization,
        byRecipient: allByRecipient,
        monthsTracked: emailStatsSnapshot.size
      }
    } catch (error) {
      console.error('❌ Error getting total email stats:', error)
      throw error
    }
  }

  // Get email statistics filtered by time period
  async getEmailStatsByPeriod(period = 'total') {
    try {
      const now = new Date()
      let startDate, endDate
      
      switch (period) {
        case 'day':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
          break
        case 'week':
          const dayOfWeek = now.getDay()
          const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Monday start
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToSubtract)
          endDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000)
          break
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1)
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1)
          break
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1)
          endDate = new Date(now.getFullYear() + 1, 0, 1)
          break
        case 'total':
        default:
          return await this.getTotalEmailStats()
      }
      
      // Get stats for the period
      const startMonth = startDate.toISOString().slice(0, 7)
      const endMonth = endDate.toISOString().slice(0, 7)
      
      return await this.getEmailStatsRange(startMonth, endMonth)
    } catch (error) {
      console.error('❌ Error getting email stats by period:', error)
      throw error
    }
  }

  // Get top email recipients
  async getTopEmailRecipients(limit = 10) {
    try {
      const totalStats = await this.getTotalEmailStats()
      const recipients = Object.entries(totalStats.byRecipient)
        .map(([email, count]) => ({ email, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
      
      return recipients
    } catch (error) {
      console.error('❌ Error getting top email recipients:', error)
      throw error
    }
  }

  // Get email statistics by organization
  async getEmailStatsByOrganization(organizationId) {
    try {
      const totalStats = await this.getTotalEmailStats()
      return {
        organizationId,
        totalEmails: totalStats.byOrganization[organizationId] || 0,
        percentage: totalStats.totalEmails > 0 
          ? Math.round((totalStats.byOrganization[organizationId] || 0) / totalStats.totalEmails * 100)
          : 0
      }
    } catch (error) {
      console.error('❌ Error getting email stats by organization:', error)
      throw error
    }
  }
}

// Create singleton instance
const emailStatsService = new EmailStatsService()
export default emailStatsService
